import { useCallback, useEffect, useRef, useState } from 'react';
import { projectsAPI } from '../services/api';

export const useExportNotifications = () => {
  const [notifications, setNotifications] = useState(() => {
    const saved = localStorage.getItem('exportNotifications');
    return saved ? JSON.parse(saved) : [];
  });

  const pollIntervalsRef = useRef({});

  const startPollingInternalRef = useRef();

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('exportNotifications', JSON.stringify(notifications));
  }, [notifications]);

  // Internal polling function (doesn't create duplicate polls)
  const startPollingInternal = useCallback(
    (jobId, projectName, format = 'mp4') => {
      // Don't start if already polling
      if (pollIntervalsRef.current[jobId]) {
        // console.log(`[Export Notifications] Already polling job: ${jobId}`);
        return;
      }

      // console.log(`[Export Notifications] Starting polling for job: ${jobId}`);

      let pollInterval = 3000;
      const MAX_POLL_INTERVAL = 10000;

      const poll = async () => {
        try {
          const response = await projectsAPI.getPuppeteerExportStatus({
            jobId,
          });
          const jobData = response.data.data || response.data;
          const { status, progress, stage, downloadUrl, error } = jobData;

          console.log(
            `[Export Notifications] Poll result for ${jobId}: ${stage} (${progress}%)`
          );

          setNotifications((prev) =>
            prev.map((n) =>
              n.jobId === jobId
                ? {
                  ...n,
                  status:
                    status === 'completed'
                      ? 'complete'
                      : status === 'failed'
                        ? 'error'
                        : 'processing',
                  progress: progress || 0,
                  stage,
                  downloadUrl,
                  error,
                  timestamp: Date.now(),
                }
                : n
            )
          );

          if (
            status === 'completed' ||
            status === 'failed' ||
            stage === 'complete' ||
            stage === 'error'
          ) {
            console.log(
              `[Export Notifications] Job ${jobId} finished with status: ${status}`
            );
            clearInterval(pollIntervalsRef.current[jobId]);
            delete pollIntervalsRef.current[jobId];
          } else {
            pollInterval = Math.min(pollInterval * 1.2, MAX_POLL_INTERVAL);
          }
        } catch (error) {
          console.error(`[Export Notifications] Poll error for ${jobId}:`, error);

          // Stop polling if the job no longer exists on the server (404)
          if (error.response && error.response.status === 404) {
            console.warn('Job not found, stopping polling');
            clearInterval(pollIntervalsRef.current[jobId]);
            delete pollIntervalsRef.current[jobId];

            setNotifications(prev => prev.map(n =>
              n.jobId === jobId ? { ...n, status: 'error', error: 'Job not found' } : n
            ));
            return;
          }

          // On other errors, slow down polling
          pollInterval = Math.min(pollInterval * 1.5, MAX_POLL_INTERVAL);
        }
      };

      // Initial poll
      poll();

      // Set up interval for subsequent polls
      pollIntervalsRef.current[jobId] = setInterval(poll, pollInterval);
    },
    []
  );

  useEffect(() => {
    startPollingInternalRef.current = startPollingInternal;
  }, [startPollingInternal]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'exportNotifications' && e.newValue) {
        try {
          const updatedNotifications = JSON.parse(e.newValue);
          console.log(
            '[Export Notifications] Syncing from another tab:',
            updatedNotifications
          );

          setNotifications(updatedNotifications);

          // Start polling for any new jobs that we're not already polling
          updatedNotifications.forEach((notification) => {
            if (
              notification.status === 'processing' &&
              !pollIntervalsRef.current[notification.jobId]
            ) {
              console.log(
                `[Export Notifications] Starting polling for job from other tab: ${notification.jobId}`
              );

              if (startPollingInternalRef.current) {
                startPollingInternalRef.current(
                  notification.jobId,
                  notification.projectName,
                  notification.format
                );
              }
            }
          });
        } catch (error) {
          console.error(
            '[Export Notifications] Failed to parse storage update:',
            error
          );
        }
      }
    };

    // Add event listener for storage changes
    window.addEventListener('storage', handleStorageChange);

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Public polling function (adds notification + starts polling)
  const startPolling = useCallback(
    (jobId, projectName, format = 'mp4') => {
      console.log(
        `[Export Notifications] Public startPolling called for job: ${jobId}`
      );

      // Add job to notifications as "in-progress"
      setNotifications((prev) => {
        const exists = prev.find((n) => n.jobId === jobId);
        if (exists) {
          console.log(
            `[Export Notifications] Job ${jobId} already in notifications`
          );
          return prev;
        }

        return [
          ...prev,
          {
            jobId,
            projectName,
            format,
            status: 'processing',
            progress: 0,
            timestamp: Date.now(),
          },
        ];
      });

      // Start polling internally
      startPollingInternal(jobId, projectName, format);
    },
    [startPollingInternal]
  );

  // Stop polling for a job
  const stopPolling = useCallback((jobId) => {
    if (pollIntervalsRef.current[jobId]) {
      clearInterval(pollIntervalsRef.current[jobId]);
      delete pollIntervalsRef.current[jobId];
      // console.log(`[Export Notifications] Stopped polling for job: ${jobId}`);
    }
  }, []);

  // Remove a notification
  const removeNotification = useCallback(
    (jobId) => {
      setNotifications((prev) => prev.filter((n) => n.jobId !== jobId));
      stopPolling(jobId);
    },
    [stopPolling]
  );

  // Clear old notifications (older than 24 hours)
  useEffect(() => {
    const clearOldNotifications = () => {
      const now = Date.now();
      const twentyFourHours = 24 * 60 * 60 * 1000;

      setNotifications((prev) =>
        prev.filter((n) => now - n.timestamp < twentyFourHours)
      );
    };

    // Check every hour
    const interval = setInterval(clearOldNotifications, 60 * 60 * 1000);

    // Also check on mount
    clearOldNotifications();

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    console.log(
      '[Export Notifications] Checking for in-progress jobs on mount...'
    );

    notifications.forEach((notification) => {
      if (
        notification.status === 'processing' &&
        !pollIntervalsRef.current[notification.jobId]
      ) {
        // console.log(
        //   `[Export Notifications] Resuming polling for job: ${notification.jobId}`
        // );
        startPollingInternal(
          notification.jobId,
          notification.projectName,
          notification.format
        );
      }
    });
  }, []); // Only on mount

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      Object.values(pollIntervalsRef.current).forEach((interval) => {
        clearInterval(interval);
      });
    };
  }, []);

  return {
    notifications,
    startPolling,
    stopPolling,
    removeNotification,
  };
};
